import { useEffect, useRef, useState } from 'react'
import { geocode } from '../../lib/geoClient'

/**
 * What a confirmed lookup writes back to `household`. Town + ZIP as the user typed
 * them, plus the ZIP-centroid coordinates the geocoder returned. There is no
 * `origin_address` column and there will not be one (src/lib/types.ts § Privacy).
 */
export interface ConfirmedOrigin {
  origin_town: string
  origin_zip: string
  origin_lat: number
  origin_lng: number
}

export interface AddressFieldProps {
  /** Persisted household.origin_town, or null. */
  town: string | null
  /** Persisted household.origin_zip, or null. */
  zip: string | null
  /** Persisted household.origin_lat / origin_lng. Both set = confirmed. */
  lat: number | null
  lng: number | null
  disabled?: boolean
  onConfirm: (value: ConfirmedOrigin) => void
}

type Lookup =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'match'; formatted: string; lat: number; lng: number }
  | { kind: 'error'; message: string }

/**
 * Privacy: town + ZIP only. There is deliberately no street-address input —
 * ZIP-centroid granularity is all the drive-time math needs (CLAUDE.md § Privacy).
 */
export function AddressField({
  town,
  zip,
  lat,
  lng,
  disabled = false,
  onConfirm,
}: AddressFieldProps) {
  const confirmed = lat !== null && lng !== null && (town !== null || zip !== null)

  const [editing, setEditing] = useState(!confirmed)
  const [townDraft, setTownDraft] = useState(town ?? '')
  const [zipDraft, setZipDraft] = useState(zip ?? '')
  const [lookup, setLookup] = useState<Lookup>({ kind: 'idle' })
  const townRef = useRef<HTMLInputElement>(null)
  const lastQueried = useRef<string | null>(null)

  // Keep the inputs in step with the row (first load, or an organizer switching
  // households) without stomping on whatever the user is currently typing.
  useEffect(() => {
    setTownDraft((d) => (d === '' ? (town ?? '') : d))
  }, [town])
  useEffect(() => {
    setZipDraft((d) => (d === '' ? (zip ?? '') : d))
  }, [zip])

  // Once the row loads (or a save lands) with a confirmed origin, collapse to the
  // confirmed view unless the user is mid-edit.
  useEffect(() => {
    if (confirmed && lookup.kind === 'idle') setEditing(false)
  }, [confirmed, lookup.kind])

  const cleanTown = townDraft.trim()
  const cleanZip = zipDraft.trim()
  const query = `${cleanTown}${cleanTown && cleanZip ? ', ' : ''}${cleanZip}`
  const canLookup = cleanTown.length > 0 && cleanZip.length > 0

  async function runLookup() {
    if (disabled || !canLookup) return
    if (lookup.kind === 'loading') return
    if (lastQueried.current === query && lookup.kind !== 'idle') return
    lastQueried.current = query
    setLookup({ kind: 'loading' })
    try {
      // geocode() resolves to null for "no match" and only throws on a real
      // failure — the two need different copy, so branch before using the result.
      const r = await geocode(query)
      if (r === null) {
        setLookup({ kind: 'error', message: "We couldn't find that town and ZIP." })
        return
      }
      setLookup({ kind: 'match', formatted: r.formatted_address, lat: r.lat, lng: r.lng })
    } catch {
      setLookup({ kind: 'error', message: "The lookup didn't go through." })
    }
  }

  function acceptMatch() {
    if (lookup.kind !== 'match') return
    // Store what the user typed, never the geocoder's formatted string — that can
    // carry a street line back into the database.
    onConfirm({
      origin_town: cleanTown,
      origin_zip: cleanZip,
      origin_lat: lookup.lat,
      origin_lng: lookup.lng,
    })
    setLookup({ kind: 'idle' })
    setEditing(false)
  }

  function rejectMatch() {
    // Nothing is saved. Hand focus back to the user.
    setLookup({ kind: 'idle' })
    lastQueried.current = null
    townRef.current?.focus()
  }

  if (confirmed && !editing) {
    const shown = [town, zip].filter((s) => s !== null && s !== '').join(' ')
    return (
      <div>
        <p className="spz-confirmed">{shown || 'Location confirmed'}</p>
        <p className="spz-hint">Town &amp; ZIP confirmed. We never store your street address.</p>
        <button
          type="button"
          className="spz-btn spz-btn-quiet"
          disabled={disabled}
          onClick={() => {
            setEditing(true)
            setLookup({ kind: 'idle' })
            lastQueried.current = null
          }}
        >
          Change
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="spz-row">
        <label className="spz-field spz-grow">
          <span className="spz-label">Town or city</span>
          <input
            ref={townRef}
            type="text"
            inputMode="text"
            autoComplete="address-level2"
            className="spz-input"
            placeholder="Reston"
            value={townDraft}
            disabled={disabled}
            onChange={(e) => setTownDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void runLookup()
              }
            }}
          />
        </label>
        <label className="spz-field spz-zip">
          <span className="spz-label">ZIP</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={10}
            className="spz-input"
            placeholder="20191"
            value={zipDraft}
            disabled={disabled}
            onChange={(e) => setZipDraft(e.target.value)}
            onBlur={() => void runLookup()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void runLookup()
              }
            }}
          />
        </label>
      </div>

      <p className="spz-hint">Town and ZIP only — no street address, ever.</p>

      <button
        type="button"
        className="spz-btn"
        disabled={disabled || !canLookup || lookup.kind === 'loading'}
        onClick={() => void runLookup()}
      >
        {lookup.kind === 'loading' ? 'Looking up…' : 'Look up'}
      </button>

      {lookup.kind === 'loading' && (
        <p className="spz-hint" role="status">
          <span className="spz-spinner" aria-hidden="true" /> Checking that location…
        </p>
      )}

      {lookup.kind === 'match' && (
        <div className="spz-match" role="group" aria-label="Confirm location">
          <p className="spz-match-text">{lookup.formatted}</p>
          <div className="spz-row spz-row-tight">
            <button type="button" className="spz-btn spz-btn-primary" onClick={acceptMatch}>
              Looks right
            </button>
            <button type="button" className="spz-btn spz-btn-quiet" onClick={rejectMatch}>
              Edit
            </button>
          </div>
        </div>
      )}

      {lookup.kind === 'error' && (
        <p className="spz-error" role="alert">
          {lookup.message} Check the spelling and try again.
        </p>
      )}
    </div>
  )
}

export default AddressField
